import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
    PaginationEllipsis,
} from "@/components/ui/pagination";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { getNativePlatform } from "@/lib/runtime";

function getPages(curr, total) {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    if (curr <= 3) return [1, 2, 3, 4, "…", total];
    if (curr >= total - 2) return [1, "…", total - 3, total - 2, total - 1, total];
    return [1, "…", curr - 1, curr, curr + 1, "…", total];
}

export function PaginationBar({ page, pageCount, onPageChange }) {
    if (pageCount <= 1) return null;

    const pages = getPages(page, pageCount);
    const isNativeAndroid = getNativePlatform() === "android";

    if (isNativeAndroid) {
        const nativePages = pageCount <= 5
            ? Array.from({ length: pageCount }, (_, index) => index + 1)
            : page <= 3
                ? [1, 2, 3, "ellipsis-end", pageCount]
                : page >= pageCount - 2
                    ? [1, "ellipsis-start", pageCount - 2, pageCount - 1, pageCount]
                    : [1, "ellipsis-start", page, "ellipsis-end", pageCount];

        return (
            <nav className="native-v3-pagination" aria-label="Sidnavigering">
                <button
                    type="button"
                    className="native-v3-pagination-direction"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    aria-label="Föregående sida"
                >
                    <ChevronLeft aria-hidden="true" />
                    <span>Föregående</span>
                </button>

                <div className="native-v3-pagination-pages">
                    {nativePages.map((item, index) =>
                        typeof item === "string" ? (
                            <span className="native-v3-pagination-ellipsis" key={`${item}-${index}`}>
                                <MoreHorizontal aria-hidden="true" />
                            </span>
                        ) : (
                            <button
                                type="button"
                                key={item}
                                className="native-v3-pagination-page"
                                data-active={item === page ? "true" : "false"}
                                aria-current={item === page ? "page" : undefined}
                                aria-label={`Sida ${item}`}
                                onClick={() => onPageChange(item)}
                            >
                                {item}
                            </button>
                        )
                    )}
                </div>

                <button
                    type="button"
                    className="native-v3-pagination-direction"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= pageCount}
                    aria-label="Nästa sida"
                >
                    <span>Nästa</span>
                    <ChevronRight aria-hidden="true" />
                </button>
            </nav>
        );
    }

    return (
        <Pagination className="mt-4">
            <PaginationContent>
                <PaginationItem>
                    <PaginationPrevious
                        onClick={() => onPageChange(page - 1)}
                        aria-disabled={page <= 1}
                        className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                    />
                </PaginationItem>

                {pages.map((p, idx) =>
                    p === "…" ? (
                        <PaginationItem key={`e-${idx}`}>
                            <PaginationEllipsis />
                        </PaginationItem>
                    ) : (
                        <PaginationItem key={p}>
                            <PaginationLink
                                isActive={p === page}
                                onClick={() => onPageChange(p)}
                            >
                                {p}
                            </PaginationLink>
                        </PaginationItem>
                    )
                )}

                <PaginationItem>
                    <PaginationNext
                        onClick={() => onPageChange(page + 1)}
                        aria-disabled={page >= pageCount}
                        className={page >= pageCount ? "pointer-events-none opacity-50" : ""}
                    />
                </PaginationItem>
            </PaginationContent>
        </Pagination>
    );
}
